import {Form, Select} from 'antd';
import {useEffect, useState} from 'react';
import styles from './pushNotificationData.module.scss';
import {FinancialInstrumentPicker} from 'components/financialInstrumentPicker';

/**
 * Notification destination type -> instrument picker target.
 *
 * Every one of these submits a top-level `instrumentId`. `SINGLE_STOCK` used
 * to pick from a *company* list, which the handover's first rule forbids: the
 * destination is the financial instrument, never its issuer.
 *
 * `SINGLE_PUBLIC_COMPANY` is a mobile alias of `SINGLE_STOCK`. It is mapped
 * here so an existing record hydrates onto the same control, but it is not
 * offered as a second user-facing option.
 */
const INSTRUMENT_DESTINATIONS = {
  SINGLE_STOCK: 'PUBLIC_EQUITY',
  SINGLE_PUBLIC_COMPANY: 'PUBLIC_EQUITY',
  SINGLE_BOND: 'BOND',
  SINGLE_MUTUAL_FUND: 'MUTUAL_FUND',
  SINGLE_IPO: 'IPO',
  SINGLE_VENTURE: 'PRIVATE_EQUITY',
  SINGLE_VENTURE_FUND: 'VENTURE_FUND',
};

export const isInstrumentDestination = type =>
  Boolean(INSTRUMENT_DESTINATIONS[type]);

const PushNotificationData = ({
  data,
  form,
  type,
  handleReportMetaData,
  handleOpportunityMetaData,
  handleCompanyMetaData,
  handleGroupMetaData,
}) => {
  const [companyId, setCompanyId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [reportId, setReportId] = useState('');
  const [groupId, setGroupId] = useState('');
  const instrumentId = Form.useWatch('instrumentId', form);

  useEffect(() => {
    // Switching to an instrument destination clears metadata a previous
    // company/report/group/opportunity selection left behind. The backend
    // builds flat `{type, instrumentId}` metadata for instrument routes, so
    // the admin must not send one of its own.
    if (isInstrumentDestination(type)) {
      handleCompanyMetaData({});
      handleReportMetaData({});
      handleOpportunityMetaData({});
      handleGroupMetaData({});
      return;
    }
    switch (type) {
      case 'COMPANY_BY_ID': {
        const company = data?.getAllCompanies?.filter(
          data => data?.id === companyId,
        );
        handleCompanyMetaData({
          companyId: company[0]?.id,
          companyName: company[0]?.name,
          imageUrl: company[0]?.logoImgURL,
          type: 'COMPANY_BY_ID',
        });
        handleGroupMetaData({});
        handleReportMetaData({});
        handleOpportunityMetaData({});
        break;
      }
      case 'REPORT_BY_ID': {
        const report = data?.getAllReports?.filter?.(
          data => data?.id === reportId,
        );
        handleReportMetaData({
          documentLink: report[0]?.document,
          reportId: report[0]?.id,
          reportType: report[0]?.type,
          title: report[0]?.title,
          type: 'REPORT_BY_ID',
        });
        handleGroupMetaData({});
        handleOpportunityMetaData({});
        handleCompanyMetaData({});
        break;
      }
      case 'OPPORTUNITY_BY_ID': {
        const opportunity = data?.getAllCampaigns?.campaigns?.filter(
          data => data?.id === opportunityId,
        );
        handleOpportunityMetaData({
          campaignId: opportunity[0]?.id,
          campaignMode: opportunity[0]?.campaignMode,
          companyName: opportunity[0]?.fundraisingCompany?.companyName,
          listingType: opportunity[0]?.listingType,
          type: 'OPPORTUNITY_BY_ID',
        });
        handleGroupMetaData({});
        handleReportMetaData({});
        handleCompanyMetaData({});
        break;
      }
      case 'GROUP_BY_ID': {
        const group = data?.getAllGroups?.groups?.filter(
          data => data?.id === groupId,
        );
        handleGroupMetaData({
          groupId: group[0]?.id,
          groupName: group[0]?.name,
          imageUrl: group[0]?.bannerImg,
          type: 'GROUP_BY_ID',
        });
        handleOpportunityMetaData({});
        handleCompanyMetaData({});
        handleReportMetaData({});
      }
    }
  }, [companyId, opportunityId, instrumentId, reportId, groupId, type]);

  return (
    <div>
      {/* Company social profile only — a stock destination is an instrument. */}
      {type === 'COMPANY_BY_ID' && (
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Selecting a Company is Required',
            },
          ]}
          name="company"
          label="Company">
          <Select
            style={{width: 730}}
            showSearch
            value={companyId}
            onChange={e => setCompanyId(e)}
            placeholder="Select a Company"
            filterOption={(inputValue, option) =>
              option.children.toLowerCase().includes(inputValue.toLowerCase())
            }>
            {data?.getAllCompanies?.map(data => (
              <Select.Option key={data?.id}>{data?.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {type === 'GROUP_BY_ID' && (
        <Form.Item name="group" label="Group">
          <Select
            style={{width: 730}}
            showSearch
            value={groupId}
            onChange={e => setGroupId(e)}
            placeholder="Select a Group"
            filterOption={(inputValue, option) =>
              option.children.toLowerCase().includes(inputValue.toLowerCase())
            }>
            {data?.getAllGroups?.groups?.map(data => (
              <Select.Option key={data?.id}>{data?.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {type === 'REPORT_BY_ID' && (
        <Form.Item name="report" label="Reports">
          <Select
            style={{width: 730}}
            value={reportId}
            onChange={e => setReportId(e)}
            showSearch
            placeholder="Select a Report"
            filterOption={(inputValue, option) =>
              option.children.toLowerCase().includes(inputValue.toLowerCase())
            }>
            {data?.getAllReports?.map(data => (
              <Select.Option key={data?.id}>{data?.title}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {type === 'SINGLE_COLLECTION' && (
        <Form.Item name="collectionId" label="Select a Collection">
          <Select
            style={{width: 730}}
            showSearch
            placeholder="Select a Collection">
            {data?.getAllCollections?.data?.collections?.map(data => (
              <Select.Option key={data?.id}>{data?.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {type === 'OPPORTUNITY_BY_ID' && (
        <Form.Item name="opportunity" label="Opportunity">
          <Select
            style={{width: 730}}
            showSearch
            value={opportunityId}
            onChange={e => setOpportunityId(e)}
            placeholder="Select a Opportunity">
            {data?.getAllCampaigns?.campaigns
              ?.filter(
                data =>
                  data?.campaignMode === 'LIVE' &&
                  data?.campaignStatus !== 'DRAFT',
              )
              ?.map(data => (
                <Select.Option
                  key={data?.id}
                  // eslint-disable-next-line react/no-children-prop
                  children={
                    <div className={styles['option-data']}>
                      <div className={styles['name-data']}>
                        <img
                          src={data?.fundraisingCompany?.logoImgURL}
                          alt=""
                        />
                        <span>{data?.fundraisingCompany?.companyName}</span>
                      </div>
                      <div>--{data?.campaignStatus}</div>
                    </div>
                  }
                />
              ))}
          </Select>
        </Form.Item>
      )}
      {isInstrumentDestination(type) && (
        <Form.Item
          rules={[{required: true, message: 'Select an instrument'}]}
          name="instrumentId"
          label="Instrument"
          extra="The destination is the instrument itself. The backend derives its instrument key and asset class.">
          <FinancialInstrumentPicker
            targetKind={INSTRUMENT_DESTINATIONS[type]}
            value={instrumentId}
            onChange={next => form.setFieldsValue({instrumentId: next})}
          />
        </Form.Item>
      )}
    </div>
  );
};

export default PushNotificationData;
